// Thin wrapper around better-auth's HTTP endpoints. We don't pull in
// better-auth's client package - it's a few fetch calls and avoids
// carrying another dependency into the Cloudflare bundle.
//
// All endpoints assume:
//   - The API sets a session cookie (HttpOnly, SameSite=Lax, Secure in prod).
//   - `credentials: 'include'` on every request so the browser sends it.
//   - The API's CORS config already allows this origin (server.ts).

import { useEffect, useState } from 'react';
import { env } from './env';

// Sentinel error class so callers can distinguish "needs verification"
// from generic auth failures without string-matching error messages.
export class EmailNotVerifiedError extends Error {
  constructor() {
    super('Check your email first - we sent you a verification link.');
    this.name = 'EmailNotVerifiedError';
  }
}

export type Role = 'producer' | 'ar' | 'admin';
export type Plan = 'free' | 'paid';

export type SessionUser = {
  id: string;
  email: string;
  handle: string | null;
  role: Role;
  plan: Plan;
};

const base = env.VITE_API_URL.replace(/\/$/, '');

// better-auth returns JSON error bodies like { message, code }. Parse the
// code into a friendly sentence; fall back to the server-provided message
// if the code is unknown, and only then to a generic sentence.
const FRIENDLY_AUTH_ERRORS: Record<string, string> = {
  INVALID_EMAIL_OR_PASSWORD: 'Incorrect email or password.',
  USER_ALREADY_EXISTS: 'An account with that email already exists.',
  EMAIL_ALREADY_EXISTS: 'An account with that email already exists.',
  WEAK_PASSWORD: 'Password is too weak. Use at least 8 characters.',
  INVALID_EMAIL: "That email address doesn't look right.",
  // Better-auth surfaces this when our DB unique-handle constraint rejects
  // the insert (the "name" field maps to users.handle).
  FAILED_TO_CREATE_USER: 'That handle is already taken. Try a different one.',
};

// Codes better-auth emits when the account exists but is not yet verified.
const EMAIL_NOT_VERIFIED_CODES = new Set(['EMAIL_NOT_VERIFIED', 'UNVERIFIED_EMAIL']);

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${base}${path}`, {
    ...init,
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    const raw = await res.text();
    let code: string | undefined;
    let message = raw.slice(0, 200);
    try {
      const parsed = JSON.parse(raw) as { message?: string; code?: string };
      code = parsed.code;
      const friendly = code ? FRIENDLY_AUTH_ERRORS[code] : undefined;
      message = friendly ?? parsed.message ?? message;
    } catch {
      // Non-JSON body: keep the raw text snippet.
    }
    if (!message) message = res.status === 401 ? 'Not signed in.' : 'Something went wrong.';
    if (code && EMAIL_NOT_VERIFIED_CODES.has(code)) throw new EmailNotVerifiedError();
    throw new Error(message);
  }
  return (await res.json()) as T;
}

// When requireEmailVerification is true, better-auth returns the user but
// token is null (no session is created until the email is confirmed).
export type SignUpResult = { user: SessionUser; token: string | null };

export async function signUpEmail(input: {
  email: string;
  password: string;
  name: string; // better-auth expects "name"; we route it to users.handle server-side
}): Promise<SignUpResult> {
  const result = await request<SignUpResult>('/auth/sign-up/email', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  // Capture fingerprint on signup too. Same lazy-import + fail-silent
  // pattern as signinEmail above.
  import('./fingerprint')
    .then(({ captureAndPostFingerprint }) => captureAndPostFingerprint())
    .catch(() => {});
  return result;
}

/**
 * Re-send the verification email. better-auth's endpoint expects the email
 * address in the request body and returns 200 on success.
 */
export async function sendVerificationEmail(email: string): Promise<void> {
  await request('/auth/send-verification-email', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/**
 * Kick off the password-reset flow. better-auth emails the user a link
 * pointing at the API's reset-password endpoint, which validates the token
 * and bounces them to `redirectTo` (our /auth/reset-password page) with the
 * token still present. Always returns 200 to avoid leaking which emails
 * exist - the caller should show "if that email is registered, we sent a
 * link" regardless of the response.
 */
export async function forgetPassword(email: string): Promise<void> {
  await request('/auth/request-password-reset', {
    method: 'POST',
    body: JSON.stringify({
      email,
      redirectTo: `${window.location.origin}/auth/reset-password`,
    }),
  });
}

/**
 * Complete the password-reset flow. `token` comes from the query string the
 * API redirects to after validating the email link.
 */
export async function resetPassword(input: {
  token: string;
  newPassword: string;
}): Promise<void> {
  await request('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function signInEmail(input: {
  email: string;
  password: string;
}): Promise<{ user: SessionUser }> {
  const result = await request<{ user: SessionUser }>('/auth/sign-in/email', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  clearSessionCache();
  // Best-effort fingerprint capture for the anti-smurf cluster guard.
  // Lazy-import so the chunk only loads on a code path the user took
  // anyway, and so it can never block the signin flow.
  import('./fingerprint')
    .then(({ captureAndPostFingerprint }) => captureAndPostFingerprint())
    .catch(() => {});
  return result;
}

/**
 * Redirects the browser to Google's consent screen. Google calls back to
 * `${api}/auth/callback/google`, better-auth exchanges the code, sets the
 * session cookie, then redirects to `callbackURL`.
 */
/**
 * Returns true when the app is running as an installed PWA in standalone mode.
 * Used to decide whether to open OAuth in the OS browser (_blank) rather than
 * navigating the current standalone webview, which providers reject or which
 * breaks the session-cookie handoff.
 */
export function isPwaStandalone(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(display-mode: standalone)').matches;
}

// better-auth's /sign-in/social is POST-only and returns JSON
// { url: 'https://provider/...consent...' } that the browser must navigate to.
//
// Caller pattern: prefetch the URL on mount via prefetchSocialUrl(), then
// render an <a target="_blank" href={url}> for the button. This avoids the
// well-known "window.open after await is blocked as a popup" trap that
// breaks OAuth in PWA standalone mode on both Android and iOS - the click
// has to navigate synchronously without crossing a network round-trip first.
export async function prefetchSocialUrl(
  provider: 'google' | 'discord',
  callbackURL: string,
): Promise<string> {
  const r = await fetch(`${base}/auth/sign-in/social`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({ provider, callbackURL }),
  });
  if (!r.ok) {
    let message = `${provider} sign-in failed (${r.status})`;
    try {
      const body = (await r.json()) as { message?: string };
      if (body.message) message = body.message;
    } catch {}
    throw new Error(message);
  }
  const data = (await r.json()) as { url?: string; redirect?: string };
  const next = data.url ?? data.redirect ?? r.headers.get('location');
  if (!next) throw new Error(`${provider} sign-in: no redirect URL in response`);
  return next;
}

// Legacy wrappers kept so any non-auth-page caller keeps working. New auth
// pages should prefetch the URL with prefetchSocialUrl() and render a real
// <a target="_blank">.
export function signInGoogle(callbackURL = `${window.location.origin}/play`): void {
  void prefetchSocialUrl('google', callbackURL)
    .then((next) => {
      window.location.href = next;
    })
    .catch((err) => {
      console.warn('[auth] google sign-in failed:', err);
    });
}

export function signInDiscord(callbackURL = `${window.location.origin}/play`): void {
  void prefetchSocialUrl('discord', callbackURL)
    .then((next) => {
      window.location.href = next;
    })
    .catch((err) => {
      console.warn('[auth] discord sign-in failed:', err);
    });
}

export async function signOut(): Promise<void> {
  // better-auth's sign-out handler always JSON.parse()s the body, so an
  // empty POST throws SyntaxError server-side. Send an empty object.
  await request('/auth/sign-out', { method: 'POST', body: '{}' });
  clearSessionCache();
}

// The API stores the handle in `users.handle` but better-auth exposes it
// under the field it calls `name` (mapped on the server side). Normalise
// once here so UI code only has to look at `user.handle`.
type RawUser = {
  id: string;
  email: string;
  name?: string | null;
  handle?: string | null;
  role?: Role;
  plan?: Plan;
};

function normaliseUser(u: RawUser): SessionUser {
  return {
    id: u.id,
    email: u.email,
    handle: u.handle ?? u.name ?? null,
    role: u.role ?? 'producer',
    plan: u.plan ?? 'free',
  };
}

// Short-lived in-memory cache for the session. Route guards (beforeLoad on
// /briefs, /scouted, /inbox, ...) call getSession() on every navigation; an
// uncached fetch to /auth/get-session blocks the route from rendering each
// time, which made those pages feel slow. The session rarely changes within a
// few seconds, so we cache it briefly and invalidate explicitly on sign-in /
// sign-out. Pass force=true to bypass the cache.
const SESSION_TTL_MS = 15_000;
let sessionCache: { at: number; value: SessionUser | null } | null = null;

export function clearSessionCache(): void {
  sessionCache = null;
}

export async function getSession(force = false): Promise<SessionUser | null> {
  if (!force && sessionCache && Date.now() - sessionCache.at < SESSION_TTL_MS) {
    return sessionCache.value;
  }
  try {
    const res = await fetch(`${base}/auth/get-session`, { credentials: 'include' });
    if (!res.ok) {
      sessionCache = { at: Date.now(), value: null };
      return null;
    }
    const body = (await res.json()) as { user?: RawUser } | null;
    const value = body?.user ? normaliseUser(body.user) : null;
    sessionCache = { at: Date.now(), value };
    return value;
  } catch {
    return null;
  }
}

export function useSession(): {
  data: SessionUser | null;
  isLoading: boolean;
  refresh: () => void;
} {
  const [data, setData] = useState<SessionUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tick, setTick] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `tick` is the re-fetch trigger
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    getSession().then((user) => {
      if (!cancelled) {
        setData(user);
        setIsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return {
    data,
    isLoading,
    refresh: () => {
      clearSessionCache();
      setTick((t) => t + 1);
    },
  };
}
